import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import * as Sentry from "@sentry/react";
import { AppContext } from "../App";
import { useConnecteeSubmissionPoll } from "../hooks/useConnecteeSubmissionPoll";
import { MATCH_STATUS, Provider, SUBMISSION_STATUS } from "../types/connecteeSubmission";
import { MatchFoundView } from "../components/submissionResult/MatchFoundView";
import { FallbackMatchesView } from "../components/submissionResult/FallbackMatchesView";
import { NoMatchesView } from "../components/submissionResult/NoMatchesView";
import {
  PollingView,
  ResultErrorView,
  TerminalStatusView,
} from "../components/submissionResult/PollingView";

function getProvidersFromMatches(
  matches: { provider?: Provider | null }[] | undefined
): Provider[] {
  if (!matches?.length) return [];
  return matches.map((match) => match.provider).filter((provider): provider is Provider => Boolean(provider));
}

export function SubmissionCreated() {
  const { submission: initialSubmission, setSubmission, setIsSubmissionCreated, setCurrentStep } =
    useContext(AppContext);

  const { submission, pollState, errorMessage } = useConnecteeSubmissionPoll(initialSubmission);
  const reportedMissingResultRef = useRef(false);

  const handleRefineSearch = useCallback(() => {
    setSubmission(null);
    setIsSubmissionCreated(false);
    setCurrentStep(0);
  }, [setSubmission, setIsSubmissionCreated, setCurrentStep]);

  const providers = useMemo(
    () => getProvidersFromMatches(submission?.connecteeMatches),
    [submission?.connecteeMatches]
  );

  useEffect(() => {
    if (pollState !== "ready" || submission) {
      reportedMissingResultRef.current = false;
      return;
    }

    if (reportedMissingResultRef.current) return;
    reportedMissingResultRef.current = true;

    Sentry.withScope((scope) => {
      scope.setTag("feature", "connectee_submission_result");
      scope.setTag("result_error_reason", "missing_result_payload");
      if (initialSubmission?.id != null) {
        scope.setTag("submission_id", String(initialSubmission.id));
      }
      Sentry.captureMessage("Connectee submission result payload missing after poll completed", "error");
    });
  }, [pollState, submission, initialSubmission?.id]);

  if (pollState === "polling") {
    return <PollingView firstName={submission?.firstName || initialSubmission?.firstName} />;
  }

  if (pollState === "timeout" || pollState === "error") {
    return (
      <ResultErrorView
        message={errorMessage || "Something went wrong while finding your match."}
        onRefineSearch={handleRefineSearch}
      />
    );
  }

  if (!submission) {
    return (
      <ResultErrorView
        message="We couldn't load your submission results."
        onRefineSearch={handleRefineSearch}
      />
    );
  }

  if (submission.status === SUBMISSION_STATUS.IN_PROGRESS) {
    const matchedProvider =
      submission.connecteeMatches?.find((match) => match.status === MATCH_STATUS.SENT_TO_PROVIDER)
        ?.provider || providers[0];

    if (matchedProvider) {
      return <MatchFoundView provider={matchedProvider} />;
    }
  }

  if (submission.status === SUBMISSION_STATUS.FALLBACK) {
    if (providers.length > 0) {
      return (
        <FallbackMatchesView
          firstName={submission.firstName}
          providers={providers}
          onRefineSearch={handleRefineSearch}
        />
      );
    }
    return <NoMatchesView onRefineSearch={handleRefineSearch} />;
  }

  return <TerminalStatusView status={submission.status} onRefineSearch={handleRefineSearch} />;
}
